import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import Confirmation from "./Confirmation";
import EditUserModal from "./EditUserModal";
import { GroupContext } from "../services/contexts/GroupContext";
import API from "../services/api";
import CreateAccount from "./CreateAccount";
import ToolkitProvider, {
  Search,
} from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import CustomButton from "./common/CustomButton";
import { contactSupportText } from "./common/utils/constants";
import { ReactComponent as UserIcon } from "../svg/UserIcon.svg";
import classes from "./UserTable.module.css";
import { truncateWithTooltip } from "../helpers/common";

function UserTable({ refetch, currentUserId }) {
  const { SearchBar } = Search;
  const { getAllUsers, globalData } = React.useContext(GroupContext);

  const [isActiveChecked, setIsActiveChecked] = React.useState(true);
  const [isDeactiveChecked, setIsDeactiveChecked] = React.useState(false);
  const [show, setShow] = React.useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [isTypeAdmin, setIsTypeAdmin] = React.useState(false);


  const sortCaret = (order) => {
    if (!order) return <span>&nbsp;</span>;
    else if (order === "asc") return <span>↓</span>;
    else if (order === "desc") return <span>↑</span>;
    return null;
  };

  const columns = [
    {
      dataField: "inactive",
      text: "Status",
      style: { width: "8%" },
      sort: true,
      sortCaret,
      formatter: (cellContent) => {
        return <span>{cellContent === 0 ? "ACTIVE" : "INACTIVE"}</span>;
      },
    },
    {
      dataField: "id",
      text: "ID",
      sort: true,
      sortCaret,
      style: { width: "8%" },
    },
    {
      dataField: "first_name",
      text: "First Name",
      sort: true,
      sortCaret,
      style: { width: "25%" },
      formatter: (cellContent) => truncateWithTooltip(cellContent),
    },
    {
      dataField: "last_name",
      text: "Last Name",
      sort: true,
      sortCaret,
      style: { width: "25%" },
      formatter: (cellContent) => truncateWithTooltip(cellContent),
    },
    {
      dataField: "username",
      text: "Username",
      sort: true,
      sortCaret,
      style: { width: "25%" },
      formatter: (cellContent) => truncateWithTooltip(cellContent),
    },
    {
      dataField: "action",
      text: "Action",
      style: { width: "20%" },
      headerStyle: {
        textAlign: 'center'
      },
      formatter: (cellContent, row) => {
        return (
          <div className={classes.actions_wrapper}>
            <EditUserModal
              userData={row}
              refetch={refetch}
              currentUserId={currentUserId}
              Children={
                <CustomButton
                  variant="outline-primary"
                  className={classes.action_button}
                >
                  Modify
                </CustomButton>
              }
            />

            <Confirmation
              content={`Please confirm ${
                row.inactive === 1 ? "activate" : "deactivate"
              } this user ?`}
              Children={
                <CustomButton
                  variant={
                    row.inactive === 1 ? "outline-success" : "outline-danger"
                  }
                  className={classes.action_button}
                >
                  {row.inactive === 0 ? "Deactivate" : "Activate"}
                </CustomButton>
              }
              onConfirm={() => {
                API.patch("/admin/updateUser", {
                  user_id: row.id,
                  first_name: row.first_name,
                  last_name: row.last_name,
                  username: row.username,
                  inactive: row.inactive === 0 ? 1 : 0,
                })
                  .catch((e) => {
                    if (e.response.data) {
                      alert(e.response.data.msg);
                    } else {
                      alert(
                        `An error occoured while updating user, ${contactSupportText}`
                      );
                    }
                  })
                  .finally(() => {
                    getAllUsers();
                  });
              }}
            />
          </div>
        );
      },
    },
  ];

  const data = useMemo(() => {
    return (isTypeAdmin ? globalData.adminUsers : globalData.memberUsers)
      ?.filter((el) => {
        if (isActiveChecked && isDeactiveChecked) {
          return el;
        } else if (!isActiveChecked && isDeactiveChecked) {
          return el.inactive === 1;
        } else if (isActiveChecked && !isDeactiveChecked) {
          return el.inactive === 0;
        }
        return false;
      })
      .sort((a, b) => a.inactive - b.inactive);
  }, [
    globalData.adminUsers,
    globalData.memberUsers,
    isActiveChecked,
    isDeactiveChecked,
    isTypeAdmin,
  ]);

  function onColumnMatch({ searchText, row }) {
    if (searchText === "") return true;

    const values = Object.values(row)
      .slice(0, 4)
      .map((el) => {
        if (typeof el === "string") {
          return el.toLowerCase();
        } else {
          return el;
        }
      });

    const searchTextArr = searchText?.toString().toLowerCase().split(" ");

    return searchTextArr.every((text) => {
      let included = false;
      values.forEach((val) => {
        if (typeof val === "string") {
          if (val?.includes(text)) included = true;
        } else {
          if (val === text) included = true;
        }
      });
      return included;
    });
  }

  function handleOnTypeSelect(e) {
    const isAdmin = e.target.value === "admin";
    setIsTypeAdmin(isAdmin);
  }

  return (
    <>
      <CustomButton
        variant="outline-primary"
        onClick={(e) => {
          e.preventDefault();
          handleShow();
        }}
        className={classes.menu_buttons}
      >
        <UserIcon width={26} height={14} style={{ margin: "0 3px 3px 0" }} />
        Users
      </CustomButton>
      <Modal
        show={show}
        onHide={handleClose}
        className="user-table-modal"
        backdrop="static"
      >
        <ToolkitProvider
          keyField="id"
          data={data}
          columns={columns}
          search={{
            onColumnMatch,
          }}
        >
          {(props) => (
            <div style={{ width: "1000px" }}>
              <Modal.Header className="_g-modal-header_">
                <div className="_g-close-button-anchor_">
                  <div className="_g-close-button_" onClick={handleClose}>
                    &#x2715;
                  </div>
                </div>
              </Modal.Header>
              <div
                style={{
                  fontWeight: "700",
                  fontSize: "26px",
                  marginBottom: "15px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ margin: "25px 40px" }}>
                  {isTypeAdmin ? "Administrators" : "Members"}{" "}
                  &nbsp;&nbsp;&nbsp;
                  {!isTypeAdmin && <CreateAccount />}
                </span>

                <div>
                  <SearchBar
                    {...props.searchProps}
                    className={classes.search_bar}
                  />
                </div>
              </div>
              <div className={classes.filter_wrapper}>
                <div className={classes.input_field_wrapper}>
                  <label>Type:</label>
                  <select
                    className={`form-select ${classes.select_field}`}
                    onChange={handleOnTypeSelect}
                    defaultValue={isTypeAdmin ? "admin" : "member"}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
                <div className={classes.input_field_wrapper}>
                  <input
                    className={classes.checkbox}
                    id="active_user"
                    checked={isActiveChecked}
                    type="checkbox"
                    onChange={() => {
                      setIsActiveChecked((prev) => !prev);
                    }}
                  />
                  <label htmlFor="active_user">Active Users</label>
                </div>
                <div className={classes.input_field_wrapper}>
                  <input
                    className={classes.checkbox}
                    id="inactive_user"
                    checked={isDeactiveChecked}
                    type="checkbox"
                    onChange={() => {
                      setIsDeactiveChecked((prev) => !prev);
                    }}
                  />
                  <label htmlFor="inactive_user">Deactive Users</label>
                </div>
              </div>
              <hr />
              <BootstrapTable
                noDataIndication="Table is Empty"
                striped
                hover
                condensed
                {...props.baseProps}
              />
            </div>
          )}
        </ToolkitProvider>
      </Modal>
    </>
  );
}

export default UserTable;
